import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Save } from 'lucide-react';
import { useCreateTemplate } from '@/hooks/useWhatsAppTemplates';
import { useToast } from '@/hooks/use-toast';
import { CreateTemplateData } from '@/types/whatsapp';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<CreateTemplateData>({
    name: '',
    category: 'GENERAL',
    content: '',
    isActive: true,
  });

  const createTemplate = useCreateTemplate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.content) {
      toast({
        title: 'Validation Error',
        description: 'Name and content are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createTemplate.mutateAsync(formData);
      toast({
        title: 'Success',
        description: 'Template created successfully',
      });
      onClose();
      setFormData({ name: '', category: 'GENERAL', content: '', isActive: true });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create template',
        variant: 'destructive',
      });
    }
  };

  const insertPlaceholder = (placeholder: string) => {
    setFormData((prev) => ({
      ...prev,
      content: prev.content + `{{${placeholder}}}`,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">Create Template</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Welcome Message"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <Select
              value={formData.category}
              onValueChange={(value: any) => setFormData((prev) => ({ ...prev, category: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="WELCOME">Welcome</SelectItem>
                <SelectItem value="FOLLOW_UP">Follow-up</SelectItem>
                <SelectItem value="REMINDER">Reminder</SelectItem>
                <SelectItem value="PROPOSAL">Proposal</SelectItem>
                <SelectItem value="NEGOTIATION">Negotiation</SelectItem>
                <SelectItem value="GENERAL">General</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template Content *
            </label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
              placeholder="Enter your template content..."
              rows={8}
              required
            />
            <p className="text-xs text-gray-500 mt-1">{formData.content.length} characters</p>
          </div>

          {/* Placeholders */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Insert Placeholders
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder('leadName')}
              >
                Lead Name
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder('companyName')}
              >
                Company Name
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder('dealValue')}
              >
                Deal Value
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => insertPlaceholder('userName')}
              >
                User Name
              </Button>
            </div>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm whitespace-pre-wrap break-words">
                {formData.content || 'Template preview will appear here...'}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createTemplate.isPending || !formData.name || !formData.content}
              className="flex-1"
            >
              <Save className="h-4 w-4 mr-2" />
              {createTemplate.isPending ? 'Creating...' : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
